import type { EmployeeTagDTO } from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { router } from "../../../../init";
import { shared } from "../../../../styles";

@customElement("ptc-employee-tags-input")
export class EmployeeTagsInput extends LitElement {
    @property({ attribute: false })
    tags: EmployeeTagDTO[] = [];

    @property({ attribute: false })
    availableTags: Readonly<EmployeeTagDTO[]> = [];

    @query("input")
    private _input: HTMLInputElement;

    @query(".results")
    private _results: HTMLDivElement;

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener("focus", () => this._focus());
        this.addEventListener("blur", () => this._blur());
    }

    private async _addTag(tag: EmployeeTagDTO) {
        if (!tag || this.tags.some((t) => t.name === tag.name)) {
            return;
        }
        this.tags.push(tag);
        this._input.value = "";
        this.requestUpdate();
        await this.updateComplete;
        this._input.focus();
        this.dispatchEvent(new CustomEvent("change", { detail: this.tags }));
    }

    private _tagClicked(tag: EmployeeTagDTO) {
        this.tags = this.tags.filter((t) => t.name !== tag.name);
        this.dispatchEvent(new CustomEvent("change", { detail: this.tags }));
    }

    private _hideResultsTimeout: number;

    private _focus() {
        window.clearTimeout(this._hideResultsTimeout);
        this._results.style.display = "";
        this.classList.add("focused");
    }

    private _blur() {
        this.classList.remove("focused");
        this._hideResultsTimeout = window.setTimeout(() => (this._results.style.display = "none"), 100);
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: row;
                gap: 0.3em;
                flex-wrap: wrap;
                border: solid 1px var(--shade-2);
                border-radius: 0.5em;
                position: relative;
                z-index: 1;
                overflow: visible;
                padding: 0.3em;
            }

            input {
                padding: 0.3em;
                border: none;
                background: none;
                width: 100%;
            }

            :host(.focused) {
                border-color: var(--color-primary);
            }

            :host(:not(.focused)) ptc-ui-icon[name="icon.action.add"] {
                opacity: 0.5;
            }

            .results {
                position: absolute;
                background: var(--color-bg);
                border: solid 1px var(--shade-2);
                width: 100%;
                border-radius: 0.5em;
                margin-top: 0.2em;
                box-sizing: border-box;
                transition: pointer-events 0.5s;
                max-width: 15em;
            }

            .add-tag {
                overflow: visible;
                position: relative;
                font-size: 0.9em;
            }

            .pill:not(:hover) > .remove-icon {
                display: none;
            }
        `,
    ];

    render() {
        const value = this._input?.value || "";
        const results = this.availableTags.filter(
            (t) =>
                !this.tags.some((t2) => t2.name === t.name) &&
                t.name !== value &&
                t.name.toLowerCase().startsWith(value)
        );

        return html`
            ${this.tags.map(
                (tag) => html`
                    <div
                        class="smaller pill click"
                        @click=${() => this._tagClicked(tag)}
                        style="--color-highlight: ${tag.color}"
                    >
                        <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon>
                        ${tag.name}
                        <ptc-ui-icon name="icon.action.remove" class="smaller remove-icon"></ptc-ui-icon>
                    </div>
                `
            )}

            <div class="smaller add-tag">
                <div class="center-aligning horizontal layout">
                    <ptc-ui-icon name="icon.action.add" @click=${() => this._input.focus()}></ptc-ui-icon>

                    <input class="stretch" placeholder="Hinzufügen..." @input=${() => this.requestUpdate()} />
                </div>

                <div class="half-padded results vertical layout" style="display: none; min-width: 20em;">
                    ${results.length
                        ? results.map(
                              (res) => html`
                                  <button
                                      class="text-left-aligning slim transparent"
                                      @click=${() => this._addTag(res)}
                                      style="--color-highlight: ${res.color}"
                                  >
                                      <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon>${res.name}
                                  </button>
                              `
                          )
                        : html` <div class="smaller padded subtle">Keine Fähigkeiten & Merkmale gefunden</div> `}

                    <div class="border-top half-margined"></div>

                    <button class="text-left-aligning slim transparent" @click=${() => router.go("settings/core-data")}>
                        <ptc-ui-icon name="icon.object.category"></ptc-ui-icon> Fähigkeiten & Merkmale Verwalten
                    </button>
                </div>
            </div>
        `;
    }
}
